import { ReactElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildErrorMessage, isUserNotAssignedToOrganisationError } from '@constants'
import { AxiosError } from 'axios'
import { Typography, Container, Button, FormControlLabel, Switch } from '@mui/material'
import { useSetPrimaryEditor } from '@api/project-members/project-members'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AddButton } from '@ui/actions'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useAppStore, useConfigStore } from '@stores'
import { getProjects } from '@queries'
import {
  createProject,
  createProjectModel,
  deleteProjectPermanently,
  duplicateProject,
  editProject,
  moveProjectToTrash,
  restoreProjectFromTrash,
} from '@mutations'
import { useHasAdminPermission } from 'src/hooks'
import { ProjectDataDialog, ProjectTable } from './components'

const Home = (): ReactElement => {
  const { user } = useAuth0()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [projectToEdit, setProjectToEdit] = useState<ProjectData | null>(null)
  const [showTrashProjects, setShowTrashProjects] = useState(false)
  // R9: the organisation's whole project list is a lot to arrive at, so it
  // opens on the user's own and the switch clears the filter.
  const showOnlyMyProjects = useAppStore(state => state.showOnlyMyProjects)
  const setShowOnlyMyProjects = useAppStore(state => state.setShowOnlyMyProjects)

  const { errors } = useConfigStore()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasAdminPermission } = useHasAdminPermission()

  const { data, isLoading, error } = useQuery(
    getProjects.getKey(showTrashProjects),
    () => getProjects.request(showTrashProjects),
    {
      onError: (error: unknown) => {
        // Not an error to shout about -- the gate below explains it instead.
        if (isUserNotAssignedToOrganisationError(error)) return

        enqueueSnackbar('Fehler beim Laden der Projekte', { variant: 'error' })
      },
    },
  )

  const isNotAssignedToOrganisation = isUserNotAssignedToOrganisationError(error)

  const invalidateProjects = async () => {
    await queryClient.invalidateQueries(['getProjects'])
  }

  const { mutate: setPrimaryEditor } = useSetPrimaryEditor<AxiosError>({
    mutation: {
      meta: {
        title: 'Bearbeiter ändern',
        description: 'Weist das Projekt einem anderen Bearbeiter zu',
      },
      onSuccess: async () => {
        await invalidateProjects()
        enqueueSnackbar('Bearbeiter geändert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern des Bearbeiters'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: handleMoveToTrash } = useMutation(moveProjectToTrash.request, {
    mutationKey: ['moveProjectToTrash'],
    onSuccess: async () => {
      await invalidateProjects()
      enqueueSnackbar('Projekt in den Papierkorb verschoben', { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(errors.deleteProject, { variant: 'error' })
    },
  })

  const { mutate: handleRestoreFromTrash } = useMutation(restoreProjectFromTrash.request, {
    mutationKey: ['restoreProjectFromTrash'],
    onSuccess: async () => {
      await invalidateProjects()
      enqueueSnackbar('Projekt wiederhergestellt', { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Wiederherstellen des Projekts', { variant: 'error' })
    },
  })

  const { mutate: handleDeletePermanently } = useMutation(deleteProjectPermanently.request, {
    mutationKey: ['deleteProjectPermanently'],
    onSuccess: async () => {
      await invalidateProjects()
      enqueueSnackbar('Projekt endgültig gelöscht', { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(errors.deleteProject, { variant: 'error' })
    },
  })

  const { mutate: handleDuplicate } = useMutation(duplicateProject.request, {
    mutationKey: duplicateProject.key,
    onMutate: async () => {
      enqueueSnackbar('Dupliziere Projekt', { variant: 'info' })
    },
    onSuccess: async () => {
      await invalidateProjects()
      enqueueSnackbar('Project erfolgreich dupliziert', { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Duplizieren des Projekts', { variant: 'error' })
    },
  })

  const [invalidError, setInvalidError] = useState<string | null>(null)

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    async (data: CreateProjectInput) => {
      const response = await createProject.request(data)

      return { data, response }
    },
    {
      onSuccess: async ({ data, response }) => {
        await invalidateProjects()
        createProjectModelMutate({ ...data, ...response })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Anlegen des Projektes'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: createProjectModelMutate, isLoading: createProjectModelLoading } = useMutation(
    (data: CreateProjectInput) => {
      return createProjectModel.request(data)
    },
    {
      mutationKey: createProjectModel.key,
      meta: {
        title: 'Modell erstellen',
        description: 'Erstellt ein Modell für das Projekt (IFC, JSON, leeres Modell)',
      },
      onSuccess: async () => {
        await invalidateProjects()
        enqueueSnackbar('Projekt erfolgreich angelegt', { variant: 'success' })
        setCreateDialogOpen(false)
      },
      onError: (error: AxiosError, data) => {
        setCreateDialogOpen(false)

        const status = error?.response?.status

        if (status === 422) {
          enqueueSnackbar(buildErrorMessage(error, 'Projekt erstellt, aber Invalides Modell'), {
            variant: 'error',
          })
        } else {
          if (data.start_mode === 'ifc') {
            enqueueSnackbar(
              buildErrorMessage(
                error,
                'Projekt erstellt, aber Fehler beim Hochladen der IFC-Datei. Bitte im Projekt erneut versuchen.',
              ),
              {
                variant: 'warning',
              },
            )
          } else if (data.start_mode === 'json') {
            enqueueSnackbar(
              buildErrorMessage(
                error,
                'Projekt erstellt, aber Fehler beim Hochladen des JSON-Modells',
              ),
              {
                variant: 'error',
              },
            )
          } else {
            enqueueSnackbar(
              buildErrorMessage(error, 'Projekt erstellt, aber Fehler beim Erstellen des Modells'),
              {
                variant: 'error',
              },
            )
          }
        }
      },
    },
  )

  const { mutate: editMutate, isLoading: editLoading } = useMutation(
    (data: ProjectData) => {
      return editProject.request(data.guid, data)
    },
    {
      onSuccess: async () => {
        await invalidateProjects()
        enqueueSnackbar('Projekt erfolgreich bearbeitet', { variant: 'success' })
        setProjectToEdit(null)
      },
      onError: (error: AxiosError) => {
        const status = error?.response?.status

        if (status === 402) {
          enqueueSnackbar(
            'Sie haben keine Projekte mehr verfügbar. Bitte wenden Sie sich an den Support um neue zu kaufen',
            { variant: 'error' },
          )
        } else {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Bearbeiten des Projektes'), {
            variant: 'error',
          })
        }
      },
    },
  )

  // Checked before the loading gate: on this error there is no data, so the
  // loading indicator would otherwise never resolve.
  if (isNotAssignedToOrganisation) {
    return (
      <Container maxWidth="lg">
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Konto nicht freigeschaltet</Typography>
          {hasAdminPermission && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/user-manager')}
              data-cy="btn-user-manager"
            >
              Benutzerverwaltung
            </Button>
          )}
        </Box>
        <Typography color="text.secondary">
          Ihr Konto ist noch keiner Organisation zugewiesen. Bitte wenden Sie sich an den Support.
        </Typography>
      </Container>
    )
  }

  if (isLoading || !data) {
    return <LoadingIndicator />
  }

  const ownProjects =
    showOnlyMyProjects && user?.sub
      ? data.filter((project: ProjectData) => project.primary_editor_guid === user.sub)
      : data

  // the trash switch swaps the list rather than extending it: either the
  // binned projects or the active ones, never both at once
  const visibleProjects = ownProjects.filter((project: ProjectData) =>
    showTrashProjects ? project.status === 'trash' : project.status !== 'trash',
  )

  return (
    <>
      <Container maxWidth="lg">
        <Box mb={3}>
          <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">Ihre Projekte {`(${visibleProjects.length})`}</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box mr={2}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showOnlyMyProjects}
                      onChange={event => setShowOnlyMyProjects(event.target.checked)}
                      data-cy="toggle-only-my-projects"
                    />
                  }
                  label="Nur meine Projekte"
                />
              </Box>
              <Box mr={2}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showTrashProjects}
                      onChange={event => setShowTrashProjects(event.target.checked)}
                      data-cy="toggle-show-trash-projects"
                    />
                  }
                  label="Papierkorb anzeigen"
                />
              </Box>
              {hasAdminPermission && (
                <Box mr={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/user-manager')}
                    data-cy="btn-user-manager"
                  >
                    Benutzerverwaltung
                  </Button>
                </Box>
              )}
              <Box mr={1}>
                <Button size="small" variant="outlined" onClick={() => navigate('/manager')}>
                  System Manager
                </Button>
              </Box>
              <AddButton
                onClick={() => setCreateDialogOpen(true)}
                data-cy="btn-create-project"
                size="small"
              >
                Neues Projekt
              </AddButton>
            </Box>
          </Box>
          <ProjectTable
            projects={visibleProjects}
            onMoveToTrash={({ guid }) => handleMoveToTrash(guid)}
            onRestoreFromTrash={({ guid }) => handleRestoreFromTrash(guid)}
            onDeletePermanently={({ guid }) => handleDeletePermanently(guid)}
            onEdit={projectData => {
              setProjectToEdit(projectData)
            }}
            onDuplicate={({ guid }) => handleDuplicate(guid)}
            onReassign={({ guid }, primaryEditorGuid) =>
              setPrimaryEditor({
                projectGuid: guid,
                data: { primary_editor_guid: primaryEditorGuid },
              })
            }
          />
        </Box>
        <Box mb={3}>
          <Typography variant="h4">Allgemein</Typography>
        </Box>
      </Container>
      {createDialogOpen && (
        <ProjectDataDialog
          open
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={createMutate}
          isLoading={createLoading || createProjectModelLoading}
          invalidError={invalidError}
          setInvalidError={setInvalidError}
          project={null}
        />
      )}
      {projectToEdit && (
        <ProjectDataDialog
          open
          onClose={() => {
            setProjectToEdit(null)
          }}
          onSubmit={values => editMutate({ ...projectToEdit, ...values })}
          isLoading={editLoading}
          invalidError={invalidError}
          setInvalidError={setInvalidError}
          project={projectToEdit}
        />
      )}
    </>
  )
}

export default Home
